import AsyncStorage from '@react-native-async-storage/async-storage'
import { useGateValue } from '@statsig/expo-bindings'
import { router, useNavigation } from 'expo-router'
import { useCallback, useEffect } from 'react'
import { Platform, View } from 'react-native'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import Logo from '@/components/navigation/Logo'
import OrganizationAvatar from '@/components/navigation/OrganizationAvatar'
import UserAvatar from '@/components/navigation/UserAvatar'
import Alert from '@/components/shared/Alert'
import Card from '@/components/shared/Card'
import Page from '@/components/shared/Page'
import UniButton from '@/components/unistyles/UniButton'
import UniText from '@/components/unistyles/UniText'
import useQueryClient from '@/hooks/useQueryClient'
import useUserRoles from '@/hooks/useUserRoles'
import { useAppStore } from '@/store/useAppStore'
import { signOut } from '@/utils/auth/auth'
import buildOrgPath from '@/utils/orgRoute'
import trpc from '@/utils/trpc'
import { AppVersions, Feature } from '../../../../../../constants'

const AccountSettings = () => {
  const { theme } = useUnistyles()
  const navigation = useNavigation()
  const { isUserAdmin, isUserOwner } = useUserRoles()
  const isEmployee = useGateValue(Feature.Employee)

  const currentOrganization = useAppStore.use.currentOrganization().organization
  const showOrganizationSwitcher = useAppStore.use.showOrganizationSwitcher()
  const setAuth = useAppStore.use.setAuth()
  const queryClient = useQueryClient()
  const resetAppTour = useAppStore.use.resetAppTour()
  const hasFirstRunExperience = useGateValue(Feature.FirstRunExperience)

  const currentUserQuery = trpc.user.current.useQuery(
    {
      organizationId: currentOrganization?.id,
    },
    {
      enabled: !!currentOrganization?.id,
    }
  )

  const userFullName = `${currentUserQuery.data?.firstName} ${currentUserQuery?.data?.lastName}`

  const handleSignOut = useCallback(async () => {
    await signOut()
    setAuth({
      accessToken: undefined,
      isLoggedIn: false,
    })

    await AsyncStorage.clear()
    queryClient.clear()
    router.replace('/')
  }, [setAuth, queryClient])

  const handleSignOutPress = useCallback(() => {
    Alert.alert('Sign Out', 'Are you sure you want to sign out?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Sign Out', style: 'destructive', onPress: handleSignOut },
    ])
  }, [handleSignOut])

  useEffect(() => {
    navigation.setOptions({
      title: 'Account',
    })
  }, [navigation])

  const handleUserSettings = () => {
    router.push(buildOrgPath('/users/settings'))
  }

  const handleManageOrg = () => {
    router.push(buildOrgPath('/org-settings/management'))
  }

  const handleSwitchOrg = () => {
    showOrganizationSwitcher()
  }

  const handleResetAppTour = () => {
    router.dismissTo(buildOrgPath('/dashboard'))
    resetAppTour()
  }

  return (
    <>
      <Page requiresSubscription={false} viewId="account-settings">
        <Logo style={{}} />

        <Card style={styles.card} title="Profile">
          <View style={styles.section}>
            <View style={styles.profile}>
              <UserAvatar
                size={64}
                user={{
                  emailHash: currentUserQuery.data?.emailHash ?? undefined,
                  firstName: currentUserQuery.data?.firstName ?? '',
                  lastName: currentUserQuery.data?.lastName ?? '',
                }}
              />
              <View style={styles.info}>
                <UniText style={styles.name} variant="titleLarge">
                  {userFullName}
                </UniText>
                <UniText style={styles.subtitle} variant="bodyMedium">
                  {currentUserQuery.data?.email}
                </UniText>
              </View>
            </View>
            <UniButton
              mode="contained"
              onPress={handleUserSettings}
              style={styles.button}
            >
              Account Settings
            </UniButton>
            <UniButton
              mode="outlined"
              onPress={handleSignOutPress}
              style={[styles.button, styles.signOutButton]}
              textColor={theme.colors.error}
            >
              Sign Out
            </UniButton>
          </View>
        </Card>

        <Card style={styles.card} title="Organization">
          <View style={styles.section}>
            <View style={styles.profile}>
              <OrganizationAvatar
                logoId={currentOrganization?.logo?.id}
                name={currentOrganization?.name || ''}
                organizationId={currentOrganization?.id || ''}
                size={64}
              />
              <View style={styles.info}>
                <UniText style={styles.name} variant="titleLarge">
                  {currentOrganization?.name}
                </UniText>
                <UniText style={styles.subtitle} variant="bodyMedium">
                  Current Organization
                </UniText>
              </View>
            </View>
            <View style={styles.actions}>
              {(isUserAdmin || isUserOwner) && (
                <UniButton
                  mode="contained"
                  onPress={handleManageOrg}
                  style={styles.button}
                >
                  Manage Organization
                </UniButton>
              )}
              <UniButton
                mode="outlined"
                onPress={handleSwitchOrg}
                style={styles.button}
              >
                Switch Organization
              </UniButton>
            </View>
          </View>
        </Card>

        {(hasFirstRunExperience || isEmployee) && (
          <Card style={styles.card} title="App Settings">
            <View style={styles.section}>
              {hasFirstRunExperience && (
                <View style={styles.settingItem}>
                  <View style={styles.settingInfo}>
                    <UniText variant="bodyLarge">Reset App Tour</UniText>
                    <UniText
                      style={styles.settingDescription}
                      variant="bodyMedium"
                    >
                      Revisit the guided tour and see helpful tips again
                    </UniText>
                  </View>
                  <UniButton
                    mode="outlined"
                    onPress={handleResetAppTour}
                    style={styles.button}
                  >
                    Reset
                  </UniButton>
                </View>
              )}

              <View style={styles.versionSection}>
                <UniText style={styles.versionTitle} variant="bodyLarge">
                  Version Information
                </UniText>
                <UniText allowFontScaling={false} style={styles.versionText}>
                  {Platform.OS === 'web'
                    ? AppVersions.expoVersion
                    : AppVersions.nativeVersion}
                  -{AppVersions.gitSha}
                </UniText>
                {isEmployee && (
                  <>
                    <UniText
                      allowFontScaling={false}
                      style={styles.versionText}
                    >
                      Git Sha: {AppVersions.gitSha}
                    </UniText>
                    <UniText
                      allowFontScaling={false}
                      style={styles.versionText}
                    >
                      Native Version: {AppVersions.nativeVersion}
                    </UniText>
                    <UniText
                      allowFontScaling={false}
                      style={styles.versionText}
                    >
                      JS Bundle Version: {AppVersions.expoVersion}
                    </UniText>
                    {Platform.OS !== 'web' && (
                      <UniText
                        allowFontScaling={false}
                        style={styles.versionText}
                      >
                        Runtime Version:{' '}
                        {AppVersions.runtimeVersion?.slice(0, 9)}
                      </UniText>
                    )}
                  </>
                )}
              </View>
            </View>
          </Card>
        )}
      </Page>
    </>
  )
}

const styles = StyleSheet.create((theme) => ({
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.tokens.spacing[3],
  },
  button: {
    minWidth: 140,
  },
  card: {
    padding: theme.tokens.spacing[4],
  },
  info: {
    flex: 1,
    gap: theme.tokens.spacing[1],
  },

  name: {
    fontWeight: '600',
  },
  profile: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.tokens.spacing[4],
  },
  section: {
    gap: theme.tokens.spacing[4],
  },
  settingDescription: {
    color: theme.colors.onSurfaceVariant,
  },
  settingInfo: {
    flex: 1,
    gap: theme.tokens.spacing[1],
  },
  settingItem: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.tokens.spacing[4],
  },
  subtitle: {
    color: theme.colors.onSurfaceVariant,
  },
  versionSection: {
    borderTopColor: theme.colors.outline,
    borderTopWidth: 1,
    gap: theme.tokens.spacing[2],
    marginTop: theme.tokens.spacing[4],
    paddingTop: theme.tokens.spacing[4],
  },
  versionText: {
    color: theme.colors.onSurfaceVariant,
    fontSize: 12,
    lineHeight: 16,
  },
  versionTitle: {
    fontWeight: '600',
    marginBottom: theme.tokens.spacing[2],
  },
  signOutButton: {
    borderColor: theme.colors.error,
  },
}))

export default AccountSettings
